import { provisionPublisherBody, type UserListItem } from '@planuze/admin-contracts';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  InlineAlert,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from '@planuze/ui';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { usePublishersContent } from '../../content/sections/publishers.js';
import { useProvisionPublisher } from '../../hooks/use-publishers.js';
import { useReservedSlugs } from '../../hooks/use-reserved-slugs.js';
import { UserPicker } from '../users/user-picker.js';
import { publisherProvisionErrorMessage } from './publisher-provision-error.js';

interface Props {
  readonly open: boolean;
  readonly onClose: () => void;
}

/* Provisiona um publisher admin-created (ADR-0483/0484): o staff escolhe o slug de um SELECT dos
   RESERVADOS (geridos no painel — ADR-0484) e nomeia um owner (usuário existente, via UserPicker).
   Este caminho é o único que atribui um namespace reservado (ex.: planuze) a um publisher; o
   onboard self-service bloqueia reservados. Sucesso → navega ao detalhe. `locked` durante a
   mutation (ADR-0216). Só super_admin abre este modal (gate na página + no backend). */
export function ProvisionPublisherModal({ open, onClose }: Props) {
  const navigate = useNavigate();
  const copy = usePublishersContent();
  const provision = useProvisionPublisher();
  const { data: reserved, isLoading: reservedLoading } = useReservedSlugs();
  const [slug, setSlug] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [owner, setOwner] = useState<UserListItem | null>(null);

  useEffect(() => {
    if (!open) {
      setSlug('');
      setDisplayName('');
      setOwner(null);
    }
  }, [open]);

  const reservedSlugs = reserved ?? [];
  const input = { slug, displayName: displayName.trim(), ownerUserId: owner?.id ?? '' };
  const valid = slug.length > 0 && provisionPublisherBody.safeParse(input).success;

  const submit = (): void => {
    provision.mutate(input, {
      onSuccess: (publisher) => {
        toast.success(copy.provisionModal.success(publisher.slug));
        onClose();
        navigate({ to: '/publishers/$id', params: { id: publisher.id } }).catch(() => undefined);
      },
      onError: (err) => toast.error(publisherProvisionErrorMessage(err, copy.provisionErrors)),
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !provision.isPending) onClose();
      }}
    >
      <DialogContent locked={provision.isPending}>
        <DialogHeader>
          <DialogTitle>{copy.provisionModal.title}</DialogTitle>
          <DialogDescription>{copy.provisionModal.description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="pp-slug">{copy.provisionModal.slugLabel}</Label>
            {reservedSlugs.length === 0 ? (
              <InlineAlert variant="info">{copy.provisionModal.noReservedSlugs}</InlineAlert>
            ) : (
              <Select value={slug} onValueChange={setSlug} disabled={reservedLoading}>
                <SelectTrigger id="pp-slug" aria-label={copy.provisionModal.slugLabel}>
                  <SelectValue placeholder={copy.provisionModal.slugPlaceholder} />
                </SelectTrigger>
                <SelectContent>
                  {reservedSlugs.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <p className="text-muted-foreground text-xs">{copy.provisionModal.slugHint}</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pp-name">{copy.provisionModal.displayNameLabel}</Label>
            <Input
              id="pp-name"
              value={displayName}
              placeholder={copy.provisionModal.displayNamePlaceholder}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pp-owner">{copy.provisionModal.ownerLabel}</Label>
            <UserPicker
              value={owner}
              onChange={setOwner}
              placeholder={copy.provisionModal.ownerPlaceholder}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={provision.isPending}>
            {copy.provisionModal.cancel}
          </Button>
          <Button loading={provision.isPending} disabled={!valid} onClick={submit}>
            {copy.provisionModal.submit}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
